import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { ISiteSegmentationContext } from './models/ISiteSegmentationContext';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { WindowToken } from '../../injection-tokens';
import { ILogger } from '@sparkware/uc-sdk-core';

@Service()
export class SiteSegmentationContext {
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _localSimpleStorageService: LocalSimpleStoreService;

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('SiteSegmentationContext');
        this._localSimpleStorageService = Container.get(LocalSimpleStoreService);
    }

    public setSiteSegmentation = async () => {
        const siteSegmentationContext = await this._generate();
        const siteSegmentationContextEncoded = this._encode(siteSegmentationContext);
        this._localSimpleStorageService.set(
            StorageItemEnum.SiteSegmentationContext,
            siteSegmentationContextEncoded,
        );
        return siteSegmentationContextEncoded;
    };

    public update = async (siteSegmentationContext: Partial<ISiteSegmentationContext>) => {
        let existingSiteSegmentation = await this.getJSON();
        if (!existingSiteSegmentation) {
            existingSiteSegmentation = await this._generate();
        }

        const updatedSiteSegmentationContext: ISiteSegmentationContext = {
            ...existingSiteSegmentation,
            ...siteSegmentationContext,
        };

        this._localSimpleStorageService.set(
            StorageItemEnum.SiteSegmentationContext,
            this._encode(updatedSiteSegmentationContext),
        );
    };

    public getString = async () => {
        const siteSegmentation = this._localSimpleStorageService.get(
            StorageItemEnum.SiteSegmentationContext,
        );
        return siteSegmentation ? siteSegmentation : await this.setSiteSegmentation();
    };

    public getJSON = async (): Promise<ISiteSegmentationContext> => {
        const siteSegmentationContextString = await this.getString();
        if (!siteSegmentationContextString) return null;

        return this._decode(siteSegmentationContextString);
    };

    private _generate = async (): Promise<ISiteSegmentationContext> => {
        const localizationData = this._window.pageContextManager.getLocalizationData();
        const userData = this._window.pageContextManager.getUserData();
        const deviceData = this._window.pageContextManager.getDeviceData();
        const infraData = this._window.pageContextManager.getInfraData();
        const envData = this._window.pageContextManager.getEnvironmentData();

        const siteSegmentationContext: ISiteSegmentationContext = {
            BankrollCurrency: localizationData.currency,
            IsAuthenticated: !!userData.cid,
            IsHybrid: deviceData.isHybrid,
            SystemId: Number(envData.systemId),
            Serial: Number(infraData.serial),
            IsUC: true,
        };
        if (userData.cid) {
            siteSegmentationContext.IsFTD = userData.isFtd;
            siteSegmentationContext.PlayerStatus = userData.statusGroupId;
        }

        return siteSegmentationContext;
    };

    private _encode = (siteSegmentationContext: ISiteSegmentationContext) =>
        encodeURIComponent(JSON.stringify(siteSegmentationContext));
    private _decode = (siteSegmentationContextString: string) =>
        JSON.parse(decodeURIComponent(siteSegmentationContextString));
}
